/**
 * Together — Michael's personal introduction page for the first circle.
 *
 * Mar 2026 · Simple-brief rewrite.
 *
 *   MICHAEL → CARE → ONE MILLION LIGHTS → HELP US → ABSORBO + SOULUMO
 *
 * The entire page is understandable in under a minute. Hierarchy
 * leads with the video, hands off to the mission, makes one clear
 * ask, and only then introduces the two apps.
 *
 * Primary action: HELP US REACH ONE MILLION LIGHTS.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { TOK, SERIF, SANS } from "../Foundations/foundationsTokens";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Config ────────────────────────────────────────────────────────
// Michael's personal video. Shipped as a placeholder tonight; swap
// VIDEO_URL to the real MP4 (or an embeddable YouTube/Vimeo URL)
// when the recording is ready.
const VIDEO_URL = ""; // set to e.g. "/media/michael-together.mp4" when ready
const VIDEO_POSTER = "/oml_preview.png";

// Absorbo lives on a sister app. Replace this URL with the real one
// (one line to edit) and the DISCOVER ABSORBO card will swap.
const ABSORBO_URL = "https://absorbo.app";

// Suggested WhatsApp-style forward message when someone taps Share.
const SHARE_URL_PATH = "/together";
const SHARE_TITLE = "One Million Lights — a little more care in the world";
const SHARE_TEXT =
  "I thought you might like this. They're trying to create One Million Lights for carers and the people they care for. ❤️";

// Pricing — pulled from the existing Soulumo Founding Light config
// (see /app/frontend/src/pages/Soulumo/FoundingLightPricing.jsx).
// Mirrored here only for display so the Reveal section doesn't need
// to mount the full pricing hero.
const FOUNDING = {
  lifetime: "£19.99",
  monthly: "£2.99",
  retail: "£14.99",
};


export default function TogetherPage() {
  const nav = useNavigate();
  const [lightsCount, setLightsCount] = useState(null);
  const [shareState, setShareState] = useState("idle"); // "idle" | "shared" | "copied"
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // ── Open Graph + Twitter metadata ────────────────────────────
  // Set at runtime so WhatsApp / in-browser previews carry the
  // mission copy even before the user interacts. Server-side
  // defaults also live in `/app/frontend/public/index.html` so the
  // link preview works for crawlers that don't execute JS.
  useEffect(() => {
    const origin = (typeof window !== "undefined" && window.location.origin) || "";
    const canonical = `${origin}${SHARE_URL_PATH}`;
    const image = `${origin}/oml_preview.png`;

    const tags = [
      ["name", "description", "£1. One light. One act of care. Help us reach one million."],
      ["property", "og:type", "website"],
      ["property", "og:url", canonical],
      ["property", "og:title", SHARE_TITLE],
      ["property", "og:description", "£1. One light. One act of care. Help us reach one million."],
      ["property", "og:image", image],
      ["property", "og:image:width", "1080"],
      ["property", "og:image:height", "1920"],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", SHARE_TITLE],
      ["name", "twitter:description", "£1. One light. One act of care. Help us reach one million."],
      ["name", "twitter:image", image],
    ];
    const originalTitle = document.title;
    document.title = SHARE_TITLE;

    const installed = [];
    for (const [attr, name, content] of tags) {
      const existing = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (existing) {
        existing.setAttribute("content", content);
        installed.push({ el: existing, remove: false });
      } else {
        const m = document.createElement("meta");
        m.setAttribute(attr, name);
        m.setAttribute("content", content);
        document.head.appendChild(m);
        installed.push({ el: m, remove: true });
      }
    }
    let linkEl = document.head.querySelector('link[rel="canonical"]');
    const linkWasNew = !linkEl;
    if (!linkEl) {
      linkEl = document.createElement("link");
      linkEl.setAttribute("rel", "canonical");
      document.head.appendChild(linkEl);
    }
    const prevHref = linkEl.getAttribute("href");
    linkEl.setAttribute("href", canonical);

    return () => {
      document.title = originalTitle;
      installed.forEach(({ el, remove }) => {
        if (remove) el.remove();
      });
      if (linkWasNew) linkEl.remove();
      else if (prevHref) linkEl.setAttribute("href", prevHref);
    };
  }, []);

  // Pull the live lights count so the OML section breathes with real
  // numbers. If the call fails we quietly ship without the badge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/one-million-lights/count`);
        if (!cancelled) setLightsCount(r.data?.count ?? r.data?.total ?? null);
      } catch (_) { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const shareUrl = useMemo(
    () => (typeof window !== "undefined" ? `${window.location.origin}${SHARE_URL_PATH}` : SHARE_URL_PATH),
    [],
  );

  const nativeShare = async () => {
    const data = { title: SHARE_TITLE, text: SHARE_TEXT, url: shareUrl };
    try {
      if (navigator.share) {
        await navigator.share(data);
        setShareState("shared");
        return;
      }
    } catch (_) { /* user cancelled or share failed — fall through to copy */ }
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT}\n${shareUrl}`);
      setShareState("copied");
    } catch (_) {
      setShareState("idle");
    }
  };

  const playVideo = () => {
    if (!VIDEO_URL) return;
    const v = videoRef.current;
    if (!v) return;
    v.play().then(() => setVideoPlaying(true)).catch(() => { /* noop */ });
  };

  const goOml = () => nav("/one-million-lights");
  const goSoulumo = () => nav("/soulumo");
  const goAbsorbo = () => { window.location.href = ABSORBO_URL; };

  const formatLights = (n) => (Number.isFinite(n) ? n.toLocaleString("en-GB") : null);

  const shareLabel =
    shareState === "shared" ? "Thank you — shared ❤" :
    shareState === "copied" ? "Link copied — paste it anywhere" :
    "Share One Million Lights";

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="together-page"
    >
      {/* ── 1 · The video ───────────────────────────────────── */}
      <section
        className="max-w-md mx-auto px-6 pt-14 pb-8 text-center"
        data-testid="together-hero"
      >
        <div
          className="relative rounded-3xl overflow-hidden mx-auto"
          style={{
            aspectRatio: "9 / 16",
            maxHeight: "min(78vh, 620px)",
            background: `#0a1428 center/cover no-repeat url('${VIDEO_POSTER}')`,
            border: `1px solid ${TOK.goldMist}`,
            boxShadow: `0 28px 72px rgba(0,0,0,0.5), 0 0 0 1px ${TOK.goldGlow}`,
          }}
          data-testid="together-video-card"
        >
          {VIDEO_URL && (
            <video
              ref={videoRef}
              src={VIDEO_URL}
              poster={VIDEO_POSTER}
              playsInline
              controls={videoPlaying}
              preload="metadata"
              className="w-full h-full object-cover"
              style={{ opacity: videoPlaying ? 1 : 0 }}
              onEnded={() => setVideoPlaying(false)}
              data-testid="together-video"
            />
          )}
          {!videoPlaying && (
            <button
              type="button"
              onClick={playVideo}
              className="absolute inset-0 flex flex-col items-center justify-center"
              style={{
                background:
                  "linear-gradient(180deg, rgba(13,31,58,0.0) 0%, rgba(13,31,58,0.0) 55%, rgba(13,31,58,0.72) 100%)",
              }}
              data-testid="together-video-play"
              aria-label="Play Michael's message"
            >
              <span
                className="flex items-center justify-center rounded-full"
                style={{
                  width: 92, height: 92,
                  background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                  border: "1px solid #f5e0c5",
                  boxShadow: `0 12px 44px ${TOK.goldGlow}`,
                }}
              >
                <span
                  style={{
                    width: 0, height: 0,
                    borderStyle: "solid",
                    borderWidth: "18px 0 18px 28px",
                    borderColor: `transparent transparent transparent #1a0f05`,
                    marginLeft: 7,
                  }}
                  aria-hidden
                />
              </span>
            </button>
          )}
        </div>

        <p
          className="mt-8 italic"
          style={{
            color: TOK.creamStrong, fontFamily: SERIF,
            fontSize: "1.1rem", lineHeight: 1.55,
          }}
          data-testid="together-subhead"
        >
          This is what we've been quietly building.
        </p>
      </section>

      {/* ── 2 · Soulumo Foundations ─────────────────────────── */}
      <section
        className="max-w-md mx-auto px-6 pt-12 pb-6"
        data-testid="together-foundations"
      >
        <div
          className="text-center uppercase"
          style={{
            color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
            letterSpacing: "0.42em",
          }}
        >
          Soulumo Foundations
        </div>
        <h2
          className="mt-4 text-center italic leading-tight"
          style={{
            color: TOK.cream, fontFamily: SERIF,
            fontSize: "clamp(1.55rem, 6vw, 2rem)", fontWeight: 300,
          }}
        >
          A Care Package for Carers.
        </h2>

        <div className="mt-8 grid grid-cols-1 gap-3">
          <div
            className="rounded-3xl p-6"
            style={{
              background: "linear-gradient(180deg, rgba(240,199,144,0.14) 0%, rgba(224,169,122,0.06) 100%)",
              border: `1px solid ${TOK.goldMist}`,
            }}
            data-testid="together-category-me"
          >
            <div
              className="uppercase"
              style={{
                color: TOK.goldWarm, fontFamily: SANS,
                fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.3em",
              }}
            >
              Care for me
            </div>
            <p
              className="mt-3"
              style={{
                color: TOK.creamStrong, fontFamily: SERIF,
                fontSize: "1rem", lineHeight: 1.5, fontStyle: "italic",
              }}
            >
              Support for the person who is doing the caring.
            </p>
          </div>
          <div
            className="rounded-3xl p-6"
            style={{
              background: "linear-gradient(180deg, rgba(245,236,217,0.06) 0%, rgba(245,236,217,0.02) 100%)",
              border: `1px solid ${TOK.goldMist}`,
            }}
            data-testid="together-category-love"
          >
            <div
              className="uppercase"
              style={{
                color: TOK.goldWarm, fontFamily: SANS,
                fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.3em",
              }}
            >
              Care for someone I love
            </div>
            <p
              className="mt-3"
              style={{
                color: TOK.creamStrong, fontFamily: SERIF,
                fontSize: "1rem", lineHeight: 1.5, fontStyle: "italic",
              }}
            >
              Simple support for being there for someone else.
            </p>
          </div>
        </div>

        <p
          className="mt-10 text-center italic"
          style={{
            color: TOK.goldSoft, fontFamily: SERIF,
            fontSize: "1.08rem", lineHeight: 1.55,
          }}
        >
          We wanted to give this away.
          <br />
          So we created One Million Lights.
        </p>
      </section>

      {/* ── 3 · One Million Lights + the ask (one moment) ───── */}
      <section
        id="together-oml"
        className="max-w-md mx-auto px-6 pt-10 pb-14 relative"
        data-testid="together-oml-section"
      >
        {/* Soft radial glow — the emotional centre. */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 -translate-x-1/2"
          style={{
            top: 0, width: 520, height: 520,
            background:
              "radial-gradient(circle, rgba(240,199,144,0.28) 0%, rgba(224,169,122,0.1) 32%, rgba(224,169,122,0) 70%)",
            filter: "blur(32px)",
          }}
        />
        <div className="relative text-center">
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
              letterSpacing: "0.42em",
            }}
          >
            One Million Lights
          </div>
          <h2
            className="mt-4 italic leading-tight"
            style={{
              color: TOK.cream, fontFamily: SERIF,
              fontSize: "clamp(1.75rem, 6.4vw, 2.15rem)", fontWeight: 300,
            }}
          >
            £1. One light. One act of care.
          </h2>
          <p
            className="mt-6"
            style={{
              color: TOK.creamStrong, fontFamily: SANS,
              fontSize: "1rem", lineHeight: 1.65,
            }}
          >
            Our goal is to create one million lights and put this care into the hands of carers and the people they care for.
          </p>

          {Number.isFinite(lightsCount) && (
            <div
              className="mt-7 mx-auto rounded-full flex items-center justify-center gap-3"
              style={{
                width: "fit-content",
                background: "rgba(224,169,122,0.08)",
                border: `1px solid ${TOK.goldMist}`,
                padding: "10px 20px",
              }}
              data-testid="together-lights-count"
            >
              <span
                aria-hidden
                className="inline-block rounded-full"
                style={{
                  width: 8, height: 8,
                  background: TOK.goldWarm,
                  boxShadow: `0 0 10px ${TOK.goldGlow}`,
                }}
              />
              <span
                className="uppercase"
                style={{
                  color: TOK.cream, fontFamily: SANS,
                  fontSize: "0.7rem", letterSpacing: "0.26em",
                }}
              >
                {formatLights(lightsCount)} lights so far
              </span>
            </div>
          )}

          <h3
            className="mt-10 uppercase"
            style={{
              color: TOK.goldWarm, fontFamily: SANS,
              fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.32em",
              lineHeight: 1.5,
            }}
          >
            Please help us reach one million lights.
          </h3>

          <ul className="mt-5 inline-block text-left space-y-1.5">
            {[
              "Give a light.",
              "Share it with your friends.",
              "Share it with your community.",
              "Send it to someone who can help us spread it further.",
            ].map((l) => (
              <li
                key={l}
                style={{
                  color: TOK.creamStrong, fontFamily: SANS,
                  fontSize: "0.95rem", lineHeight: 1.65,
                }}
              >
                — {l}
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-col gap-3">
            <button
              type="button"
              onClick={goOml}
              className="w-full rounded-full uppercase"
              style={{
                background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                border: "1px solid #f5e0c5", color: "#1a0f05",
                fontFamily: SANS, fontSize: "0.76rem", fontWeight: 700,
                letterSpacing: "0.26em", padding: "18px 22px",
                minHeight: 60,
                boxShadow: `0 12px 44px ${TOK.goldGlow}`,
              }}
              data-testid="together-cta-give-primary"
            >
              Give a light
            </button>
            <button
              type="button"
              onClick={nativeShare}
              className="w-full rounded-full uppercase"
              style={{
                background: "transparent",
                border: `1px solid ${TOK.goldMist}`,
                color: TOK.cream, fontFamily: SANS,
                fontSize: "0.72rem", fontWeight: 600,
                letterSpacing: "0.24em", padding: "15px 20px",
                minHeight: 52,
              }}
              data-testid="together-cta-share-primary"
            >
              {shareLabel}
            </button>
          </div>
        </div>
      </section>

      {/* ── 4 · And now, our first two apps are ready ───────── */}
      <section
        className="max-w-md mx-auto px-6 pt-14 pb-10"
        data-testid="together-reveal"
      >
        <div
          className="h-px w-24 mx-auto"
          style={{ background: `linear-gradient(90deg, transparent, ${TOK.goldMist}, transparent)` }}
          aria-hidden
        />
        <p
          className="mt-9 text-center italic"
          style={{
            color: TOK.creamStrong, fontFamily: SERIF,
            fontSize: "1.05rem", lineHeight: 1.55,
          }}
        >
          And after months of building, we're finally ready to introduce our first two apps to the world.
        </p>

        <div className="mt-7 grid grid-cols-1 gap-4">
          <RevealCard
            kicker="Absorbo"
            line="A different way to learn a language."
            ctaLabel="Discover Absorbo"
            onCta={goAbsorbo}
            testid="together-reveal-absorbo"
          />
          <RevealCard
            kicker="Soulumo"
            line="Personalised audio installations designed around you."
            ctaLabel="Discover Soulumo"
            onCta={goSoulumo}
            testid="together-reveal-soulumo"
          />
        </div>
      </section>

      {/* ── 5 · Founding Light thank-you ────────────────────── */}
      <section
        className="max-w-md mx-auto px-6 pt-4 pb-14"
        data-testid="together-founding"
      >
        <div
          className="rounded-3xl p-7 text-center"
          style={{
            background: "rgba(245,236,217,0.04)",
            border: `1px solid ${TOK.cardBorder}`,
          }}
        >
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
              letterSpacing: "0.42em",
            }}
          >
            Founding Light
          </div>
          <p
            className="mt-4"
            style={{
              color: TOK.creamStrong, fontFamily: SANS,
              fontSize: "0.98rem", lineHeight: 1.65,
            }}
          >
            Because you're here at the beginning, we'd like you to have our Founding Light pricing.
          </p>
          <p
            className="mt-3 italic"
            style={{
              color: TOK.goldSoft, fontFamily: SERIF,
              fontSize: "1rem", lineHeight: 1.5,
            }}
          >
            Your rate stays with you for life.
          </p>

          <div
            className="mt-6 rounded-2xl p-5"
            style={{
              background: "rgba(224,169,122,0.07)",
              border: `1px solid ${TOK.goldMist}`,
            }}
            data-testid="together-founding-pricing"
          >
            <div
              style={{
                color: TOK.cream, fontFamily: SERIF,
                fontSize: "1.8rem", fontWeight: 300,
              }}
            >
              {FOUNDING.lifetime}
              <span style={{ fontSize: "0.9rem", color: TOK.creamMist, marginLeft: 8 }}>
                lifetime
              </span>
            </div>
            <div
              className="mt-1"
              style={{
                color: TOK.creamDim, fontFamily: SANS,
                fontSize: "0.9rem",
              }}
            >
              then {FOUNDING.monthly}/month
              <span style={{ color: TOK.creamFaint, marginLeft: 8, textDecoration: "line-through" }}>
                {FOUNDING.retail}/mo retail
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={goSoulumo}
            className="mt-7 w-full rounded-full uppercase"
            style={{
              background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              border: "1px solid #f5e0c5", color: "#1a0f05",
              fontFamily: SANS, fontSize: "0.72rem", fontWeight: 700,
              letterSpacing: "0.26em", padding: "16px 22px",
              minHeight: 56,
            }}
            data-testid="together-cta-founding"
          >
            Explore your Founding access
          </button>
        </div>
      </section>

      {/* ── Final · Bring it back to the mission ────────────── */}
      <section
        className="max-w-md mx-auto px-6 pt-4 pb-20 text-center"
        data-testid="together-final"
      >
        <div
          className="h-px w-24 mx-auto"
          style={{ background: `linear-gradient(90deg, transparent, ${TOK.goldMist}, transparent)` }}
          aria-hidden
        />
        <p
          className="mt-10 italic"
          style={{
            color: TOK.cream, fontFamily: SERIF,
            fontSize: "1.1rem", lineHeight: 1.55,
          }}
        >
          More than anything, please help us spread One Million Lights.
          <br />
          Let's see how much care we can put into the world together.
        </p>

        <div className="mt-9 flex flex-col gap-3">
          <button
            type="button"
            onClick={goOml}
            className="w-full rounded-full uppercase"
            style={{
              background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              border: "1px solid #f5e0c5", color: "#1a0f05",
              fontFamily: SANS, fontSize: "0.78rem", fontWeight: 700,
              letterSpacing: "0.26em", padding: "18px 22px",
              minHeight: 60,
              boxShadow: `0 12px 44px ${TOK.goldGlow}`,
            }}
            data-testid="together-cta-give-final"
          >
            Give a light
          </button>
          <button
            type="button"
            onClick={nativeShare}
            className="w-full rounded-full uppercase"
            style={{
              background: "transparent",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.cream, fontFamily: SANS,
              fontSize: "0.72rem", fontWeight: 600,
              letterSpacing: "0.24em", padding: "15px 20px",
              minHeight: 52,
            }}
            data-testid="together-cta-share-final"
          >
            {shareLabel}
          </button>
        </div>

        <p
          className="mt-14 italic"
          style={{
            color: TOK.goldSoft, fontFamily: SERIF,
            fontSize: "1.05rem", lineHeight: 1.5,
          }}
          data-testid="together-sign-off"
        >
          With love,
          <br />
          Michael
        </p>
      </section>
    </div>
  );
}


function RevealCard({ kicker, line, ctaLabel, onCta, testid }) {
  return (
    <div
      className="rounded-3xl p-6"
      style={{
        background: "rgba(245,236,217,0.04)",
        border: `1px solid ${TOK.cardBorder}`,
      }}
      data-testid={testid}
    >
      <div
        className="uppercase"
        style={{
          color: TOK.goldWarm, fontFamily: SANS, fontSize: "0.68rem",
          fontWeight: 700, letterSpacing: "0.32em",
        }}
      >
        {kicker}
      </div>
      <p
        className="mt-3 italic"
        style={{
          color: TOK.cream, fontFamily: SERIF,
          fontSize: "1.2rem", lineHeight: 1.4, fontWeight: 300,
        }}
      >
        {line}
      </p>
      <button
        type="button"
        onClick={onCta}
        className="mt-5 w-full rounded-full uppercase"
        style={{
          background: "transparent",
          border: `1px solid ${TOK.goldSoft}`,
          color: TOK.goldWarm, fontFamily: SANS,
          fontSize: "0.7rem", fontWeight: 700,
          letterSpacing: "0.28em", padding: "13px 20px",
          minHeight: 48,
        }}
        data-testid={`${testid}-cta`}
      >
        {ctaLabel}
      </button>
    </div>
  );
}
